import { LinkOffOutlined, LinkOutlined } from '@vindr/icons';
import {
  EXPORT_TYPE,
  IMAGE_TYPE,
} from '@vindr/services/constants/viewer.constant';
import {
  Button,
  ConfigProvider,
  Form,
  Input,
  Radio,
  Select,
  Space,
  theme,
} from 'antd';
import { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Selection from './Selection';
import Switch from './Switch';
import Tooltip from './Tooltip';
import { debounce } from 'lodash';

const FILE_TYPE_OPTIONS = [
  {
    value: 'jpg',
    label: 'jpg',
  },
  {
    value: 'png',
    label: 'png',
  },
];

const EXPORT_TYPES = [
  { label: 'Current instance', value: EXPORT_TYPE.INSTANCE },
  { label: 'Current series', value: EXPORT_TYPE.SERIES },
  { label: 'Current study', value: EXPORT_TYPE.STUDY },
];

enum IMAGE_SIZE {
  CURRENT,
  CUSTOM,
}

const DEFAULT_FILENAME = 'image';

const REFRESH_VIEWPORT_TIMEOUT = 100;

const ViewportDownloadForm = ({
  activeViewportElement,
  onClose,
  updateViewportPreview,
  enableViewport,
  disableViewport,
  toggleAnnotations,
  loadImage,
  downloadBlob,
  defaultSize,
  minimumSize,
  maximumSize,
  canvasClass,
  canRenderImage,
  canDownloadRaw,
  isDownloading,
  totalSize,
  downloadedSize,
}: {
  isDownloading: boolean;
  totalSize: number;
  downloadedSize: number;
  downloadBlob: (
    fileType: string,
    exportType: EXPORT_TYPE,
    imageType: IMAGE_TYPE
  ) => void;
}) => {
  const { t } = useTranslation('ViewerTool');

  const [form] = Form.useForm();

  const [filename, setFilename] = useState(DEFAULT_FILENAME);
  const [fileType, setFileType] = useState('jpg');
  const [exportType, setExportType] = useState(EXPORT_TYPE.INSTANCE);
  const [imageType, setImageType] = useState(IMAGE_TYPE.DICOM);
  const [imageSize, setImageSize] = useState(IMAGE_SIZE.CURRENT);

  const defaultWidth = activeViewportElement?.offsetWidth || 0;
  const defaultHeight = activeViewportElement?.offsetHeight || 0;

  const width = Form.useWatch('width', form);
  const height = Form.useWatch('height', form);

  const [showAnnotations, setShowAnnotations] = useState(true);

  const [keepAspect, setKeepAspect] = useState(true);
  const [aspectMultiplier, setAspectMultiplier] = useState({
    width: defaultWidth / Math.min(defaultWidth, defaultHeight),
    height: defaultHeight / Math.min(defaultWidth, defaultHeight),
  });

  const [viewportElement, setViewportElement] = useState();

  const [downloadCanvas, setDownloadCanvas] = useState({
    ref: createRef(),
    width: defaultWidth,
    height: defaultHeight,
  });

  const [error, setError] = useState({
    width: null,
    height: null,
    filename: false,
  });

  const hasError = Object.values(error).some((it) => !!it);

  const refreshViewport = useRef(null);

  const onKeepAspectToggle = () => {
    const { width, height } = form.getFieldsValue();
    const newAspectMultiplier = { ...aspectMultiplier };

    if (!keepAspect) {
      const base = Math.min(width, height);

      newAspectMultiplier.width = width / base;
      newAspectMultiplier.height = height / base;
      setAspectMultiplier(newAspectMultiplier);
    }

    setKeepAspect(!keepAspect);
  };

  const downloadImage = () => {
    downloadBlob(fileType, exportType, imageType);
  };

  /**
   * @param {object} value - Input value
   * @param {string} dimension - "height" | "width"
   */
  const onDimensionsChange = (value, dimension) => {
    const oppositeDimension = dimension === 'height' ? 'width' : 'height';
    const sanitizedTargetValue = value.replace(/\D/, '');
    const isEmpty = sanitizedTargetValue === '';
    const updatedDimension = isEmpty ? '' : sanitizedTargetValue;

    if (updatedDimension === form.getFieldValue(dimension)) {
      return;
    }

    if (keepAspect && form.getFieldValue(oppositeDimension) !== '') {
      form.setFieldValue(oppositeDimension, Math.round(
          form.getFieldValue(dimension) * aspectMultiplier[oppositeDimension]
        ),
      );
    }

    // Only update if value is non-empty
    if (!isEmpty) {
      setDownloadCanvas({
        ...downloadCanvas,
        width: form.getFieldValue('width'),
        height: form.getFieldValue('height'),
      });
    }
  };

  const debounceDimensionsChange = debounce(onDimensionsChange, 1000);

  const error_messages = {
    width: {
      min: 'The minimum valid width is 100px.',
      max: 'The maximum valid width is 5000px.',
    },
    height: {
      min: 'The minimum valid height is 100px.',
      max: 'The maximum valid height is 5000px.',
    },
    filename: 'The file name cannot be empty.',
  };

  const renderErrorHandler = (errorType) => {
    if (!error[errorType]) {
      return null;
    }

    return (
      <div className="mt-0 mb-2 pl-1" color="error">
        {error_messages[errorType][error[errorType]]}
      </div>
    );
  };

  const validSize = useCallback(
    (value) => (value >= minimumSize ? value : minimumSize),
    [minimumSize]
  );

  const loadAndUpdateViewports = useCallback(async () => {
    const { width: scaledWidth, height: scaledHeight } = await loadImage(
      activeViewportElement,
      viewportElement,
      width,
      height
    );

    toggleAnnotations(showAnnotations, viewportElement, activeViewportElement);

    const scaledDimensions = {
      height: validSize(scaledHeight),
      width: validSize(scaledWidth),
    };

    setDownloadCanvas((state) => ({
      ...state,
      ...scaledDimensions,
    }));

  }, [
    loadImage,
    activeViewportElement,
    viewportElement,
    width,
    height,
    toggleAnnotations,
    showAnnotations,
    validSize,
    updateViewportPreview,
    downloadCanvas.ref,
    fileType,
  ]);

  useEffect(() => {
    if (imageSize === IMAGE_SIZE.CURRENT) {
      const offsetWidth = activeViewportElement?.offsetWidth || 0;
      const offsetHeight = activeViewportElement?.offsetHeight || 0;
      const newDimensions = { width: offsetWidth, height: offsetHeight };

      form.setFieldsValue({
        width: offsetWidth,
        height: offsetHeight,
      });
      setDownloadCanvas((state) => ({
        ...state,
        ...newDimensions,
      }));
    } else {
      const newDimensions = { width: defaultSize, height: defaultSize };

      form.setFieldsValue({
        width: defaultSize,
        height: defaultSize,
      });
      setDownloadCanvas((state) => ({
        ...state,
        ...newDimensions,
      }));
    }
  }, [activeViewportElement, defaultSize, imageSize]);

  useEffect(() => {
    enableViewport(viewportElement);

    return () => {
      disableViewport(viewportElement);
    };
  }, [disableViewport, enableViewport, viewportElement]);

  useEffect(() => {
    if (refreshViewport.current !== null) {
      clearTimeout(refreshViewport.current);
    }

    refreshViewport.current = setTimeout(() => {
      refreshViewport.current = null;
      loadAndUpdateViewports();
    }, REFRESH_VIEWPORT_TIMEOUT);
  }, [
    activeViewportElement,
    viewportElement,
    showAnnotations,
    loadImage,
    toggleAnnotations,
    updateViewportPreview,
    fileType,
    downloadCanvas.ref,
    minimumSize,
    maximumSize,
    loadAndUpdateViewports,
    imageSize,
  ]);

  useEffect(() => {
    const { width, height } = form.getFieldsValue();
    const hasError =
      exportType === EXPORT_TYPE.INSTANCE &&
      imageType === IMAGE_TYPE.IMAGE &&
      imageSize === IMAGE_SIZE.CUSTOM
        ? {
            width:
              width < minimumSize ? 'min' : width > maximumSize ? 'max' : null,
            height:
              height < minimumSize
                ? 'min'
                : height > maximumSize
                ? 'max'
                : null,
            filename: !filename,
          }
        : null;

    setError({ ...hasError });
  }, [
    filename,
    minimumSize,
    imageType,
    imageSize,
    maximumSize,
    exportType,
  ]);

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          fontSize: 12,
          borderRadiusSM: 2,
          borderRadiusLG: 2,
          borderRadius: 2,
          colorPrimary: '#39C2D7',
          colorPrimaryHover: '#2799b0',
          colorText: '#fff',
          colorBgBase: '#172126',
          colorBgContainer: 'transparent',
          colorBorder: '#647a87',
          controlItemBgActiveDisabled: '#2C4654',
          controlItemBgActive: '#2C4654',
          controlOutlineWidth: 0,
          colorBgElevated: '#1F313B',
        },
        components: {
          Menu: {
            colorItemBg: 'transparent',
            colorItemBgSelected: '#2c5166',
          },
        },
      }}
    >
      <div className="text-xl">
        {t(
          'Please specify the dimensions and desired type for the output image.'
        )}
      </div>

      <div className="mt-6 flex flex-col rounded border border-gray-600 p-3">
        <Form colon={true}>
          <div className="w-full">
            <Form.Item className="mb-0" label={t('Export') + ': '}>
              <Radio.Group
                className="max-sm:flex max-sm:flex-col"
                value={exportType}
                options={EXPORT_TYPES.map((it) => ({
                  ...it,
                  label: t(it.label),
                }))}
                onChange={(e) => setExportType(e.target.value)}
              />
            </Form.Item>
            {renderErrorHandler('filename')}
          </div>
          {exportType === EXPORT_TYPE.INSTANCE && (
            <div className="w-full">
              <Form.Item className="mb-0" label={t('File format') + ': '}>
                <Radio.Group
                  value={imageType}
                  onChange={(e) => setImageType(e.target.value)}
                >
                  {canRenderImage && (
                    <Radio value={IMAGE_TYPE.IMAGE}>{t('Image')}</Radio>
                  )}
                  <Radio value={IMAGE_TYPE.DICOM}>DICOM</Radio>
                  {canDownloadRaw && (
                    <Radio value={IMAGE_TYPE.RAW}>{t('Raw')}</Radio>
                  )}
                </Radio.Group>
              </Form.Item>
            </div>
          )}
        </Form>
      </div>

      {exportType === EXPORT_TYPE.INSTANCE &&
        imageType === IMAGE_TYPE.IMAGE && (
          <>
            <div className="mt-6 flex flex-col rounded border border-gray-600 p-3">
              <Form colon={true} form={form} initialValues={{ width: defaultWidth, height: defaultHeight }}>
                {/* <div className="w-full">
                  <Form.Item className="mb-0" label={t('File Name') + ': '}>
                    <Input
                      size="small"
                      data-cy="file-name"
                      value={filename}
                      onChange={(evt) => setFilename(evt.target.value)}
                    />
                  </Form.Item>
                  {renderErrorHandler('filename')}
                </div> */}
                <div className="flex">
                  <div className="flex w-1/2 flex-col">
                    <Form.Item className="mb-0" label={t('Image Size') + ': '}>
                      <Radio.Group
                        value={imageSize}
                        onChange={(e) => setImageSize(e.target.value)}
                      >
                        <Space direction="vertical">
                          <Radio value={IMAGE_SIZE.CURRENT}>
                            {t('Current size')}
                          </Radio>
                          <Radio value={IMAGE_SIZE.CUSTOM}>{t('Custom')}</Radio>
                        </Space>
                      </Radio.Group>
                    </Form.Item>
                    <div className="flex">
                      <div className="flex grow flex-col">
                        <div className="w-full">
                          <Form.Item
                            className="mb-0"
                            label={`${t('Width')} (px): `}
                            name="width"
                            colon={true}
                          >
                            <Input
                              disabled={imageSize === IMAGE_SIZE.CURRENT}
                              size="small"
                              type="number"
                              min={minimumSize}
                              max={maximumSize}
                              onChange={(evt) =>
                                debounceDimensionsChange(evt.target.value, 'width')
                              }
                              data-cy="image-width"
                            />
                          </Form.Item>
                          {renderErrorHandler('width')}
                        </div>
                        <div className="w-full">
                          <Form.Item
                            className="mb-0"
                            name="height"
                            label={`${t('Height')} (px): `}
                          >
                            <Input
                              disabled={imageSize === IMAGE_SIZE.CURRENT}
                              size="small"
                              type="number"
                              min={minimumSize}
                              max={maximumSize}
                              onChange={(evt) =>
                                debounceDimensionsChange(evt.target.value, 'height')
                              }
                              data-cy="image-height"
                            />
                          </Form.Item>
                          {renderErrorHandler('height')}
                        </div>
                      </div>

                      <div
                        className={`ml-3 flex h-full items-center ${
                          error.height ? 'pb-8' : ''
                        }`}
                      >
                        <Tooltip
                          position="right"
                          content={
                            keepAspect ? 'Dismiss Aspect' : 'Keep Aspect'
                          }
                        >
                          <div
                            className="toggle mt-2 cursor-pointer"
                            onClick={onKeepAspectToggle}
                          >
                            {keepAspect ? (
                              <LinkOutlined className="text-4xl text-primary-light" />
                            ) : (
                              <LinkOffOutlined className="text-4xl text-white" />
                            )}
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  </div>

                  <div className="ml-6 w-1/2 border-l border-secondary-dark pl-6 dark:border-secondary-light">
                    <div>
                      <Form.Item className="mb-2" label={t('File Type') + ': '}>
                        <Selection
                          className="text-white"
                          // isClearable={false}
                          value={fileType}
                          data-cy="file-type"
                          onChange={(value) => {
                            setFileType(value);
                          }}
                          // hideSelectedOptions={false}
                          placeholder={t('File Type')}
                        >
                          {FILE_TYPE_OPTIONS.map((it) => (
                            <Select.Option key={it.value} value={it.value}>
                              {it.label}
                            </Select.Option>
                          ))}
                        </Selection>
                      </Form.Item>
                    </div>
                    <div className="ml-0">
                      <label
                        htmlFor="show-annotations"
                        className="flex items-center"
                      >
                        <Switch
                          size="small"
                          checked={showAnnotations}
                          onChange={(val) => setShowAnnotations(val)}
                        />
                        <span className="ml-2 text-base">
                          {t('Show Annotations')}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </Form>
            </div>

            <div className="mt-8">
              <div
                className="border-secondary-primary rounded bg-secondary-dark p-4"
                data-cy="image-preview"
              >
                <div className="mb-2 text-xl">{t('Image preview')}</div>
                {activeViewportElement && (
                  <div
                    className="w-full overflow-y-hidden overflow-x-scroll"
                    style={{
                      height: Math.floor(
                        height /
                          window.devicePixelRatio
                      ),
                    }}
                  >
                    <div
                      className="mx-auto"
                      style={{
                        height: Math.floor(
                          height /
                            window.devicePixelRatio
                        ),
                        width: Math.floor(
                          width /
                            window.devicePixelRatio
                        ),
                      }}
                      ref={(ref) => setViewportElement(ref)}
                    ></div>
                  </div>
                )}
                {!activeViewportElement && (
                  <div className="mt-4">
                    {t('Active viewport has no displayed image')}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

      <div className="mt-4 flex items-center justify-between">
        <div className="w-1/2">
          {isDownloading && (
            <div>
              Downloaded {(downloadedSize / 1024 / 1024).toFixed(2) + 'MB'}
              {totalSize ? `/${(totalSize / 1024 / 1024).toFixed(2)}MB` : ''}
            </div>
          )}
        </div>
        <div>
          <Button
            data-cy="cancel-btn"
            disabled={isDownloading}
            onClick={onClose}
            className="px-2"
          >
            {t('Cancel')}
          </Button>
          <Button
            className="ml-2"
            disabled={hasError}
            onClick={downloadImage}
            loading={isDownloading}
            type="primary"
            data-cy="download-btn"
          >
            {t('Export')}
          </Button>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default ViewportDownloadForm;
